// goalModel.js
// What a goal IS on screen — its status, progress, pace and dates — worked out
// once here so the left panel, the goals view and the right rail never
// disagree. Pure functions over the goals API's shape (backend/routes/goals.py).

// ── Dates ─────────────────────────────────────────────────────────────────
// Due and start dates are calendar dates ("2026-12-15", or an ISO timestamp
// whose first ten characters are one). They are read as LOCAL dates: parsing
// "2026-12-15" with new Date() reads it as UTC midnight, which is the evening
// of the 14th anywhere west of Greenwich — the goal then shows a day early.

export const pad2 = (n) => String(n).padStart(2, '0');
export const isoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export function parseDay(value) {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export const today = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
export const todayIso = () => isoDay(today());

/** Whole days from a to b (both dates or day strings); b later is positive. */
export function daysBetween(a, b) {
  const da = a instanceof Date ? a : parseDay(a);
  const db = b instanceof Date ? b : parseDay(b);
  if (!da || !db) return null;
  return Math.round((db - da) / 864e5);
}

/**
 * The local calendar day of a server timestamp (created_at, completed_at).
 * Those are UTC with no zone marker, so they are read as UTC: a goal created
 * at 8 pm in Chicago is "Oct 31" there, not the "Nov 1" its UTC date says.
 */
export function stampDay(value) {
  if (!value) return null;
  const s = String(value);
  if (!s.includes('T')) return parseDay(s) ? s.slice(0, 10) : null; // already a calendar date
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? parseDay(s) && s.slice(0, 10) : isoDay(d);
}

export function fmtDay(value) {
  const d = parseDay(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

export function fmtStamp(value) {
  if (!value) return '';
  // Server timestamps are UTC with no zone marker; say so, or they read as local.
  const s = String(value);
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) || !s.includes('T') ? s : `${s}Z`);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • `
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// ── Vocabulary ────────────────────────────────────────────────────────────

export const METRICS = {
  words: { label: 'Word count', unit: 'words', icon: 'book', ambitious: 2500, auto: true },
  tasks: { label: 'Task checklist', unit: 'tasks', icon: 'type', ambitious: 8, auto: true },
  chapters: { label: 'Chapters', unit: 'chapters', icon: 'layers', ambitious: 3 },
  pages: { label: 'Pages revised', unit: 'pages', icon: 'pen', ambitious: 40 },
  images: { label: 'Images', unit: 'images', icon: 'image', ambitious: 10 },
  sessions: { label: 'Writing sessions', unit: 'sessions', icon: 'clock', ambitious: 3 },
};

export const TEMPLATES = [
  { metric: 'words', label: 'Write words', title: 'Draft the Book', summary: 'Write and complete your first draft chapter by chapter.', target: 2000 },
  { metric: 'chapters', label: 'Finish chapters', title: 'Finish Part One', summary: 'Complete every chapter in the first part.', target: 5 },
  { metric: 'pages', label: 'Revise pages', title: 'Revise the Opening', summary: 'Tighten the first fifty pages.', target: 50 },
  { metric: 'images', label: 'Add images', title: 'Add Pictures', summary: 'Source and place images for the book.', target: 20 },
  { metric: 'tasks', label: 'Checklist', title: 'Format the Book', summary: 'Apply the theme and finalize front matter.', target: 3 },
  { metric: 'sessions', label: 'Habit', title: 'Write Five Days a Week', summary: 'Build a steady writing routine.', target: 20 },
];

export const ICONS = ['book', 'image', 'type', 'bulb', 'pen', 'layers', 'clock', 'flag'];

export const STATUS_LABEL = {
  notstarted: 'Not Started',
  progress: 'In Progress',
  risk: 'At Risk',
  overdue: 'Overdue',
  done: 'Completed',
  cancelled: 'Cancelled',
};

export const QUOTES = {
  words: 'Every word moves you closer to your dream.',
  images: 'A picture is a chapter your reader feels.',
  tasks: 'Small finished things build finished books.',
  chapters: 'One chapter at a time is still a book.',
  pages: 'Revision is where the story finds its shape.',
  sessions: 'Show up today; the book shows up tomorrow.',
  none: 'Small finished things build finished books.',
};

// ── Goals ─────────────────────────────────────────────────────────────────

export const flattenGoals = (milestones) => (milestones || []).flatMap((ms) => ms.tasks || []);

const serverState = (g) => g.status || (g.completed ? 'completed' : 'unstarted');
const isClosed = (g) => ['completed', 'cancelled', 'rejected'].includes(serverState(g));

/**
 * {cur, target, pct, unit} for a measured goal, else null. A word goal whose
 * count the server has not recorded yet reads the editor's live count.
 * @param {object} live - { total, chapters: [{id, words}] } from the editor
 */
export function progressOf(goal, live) {
  const p = goal.progress;
  if (!p) return null;
  let cur = p.current;
  if (cur == null && goal.metric === 'words' && live) {
    cur = goal.scope && goal.scope !== 'book'
      ? (live.chapters.find((c) => c.id === goal.scope)?.words || 0)
      : live.total;
  }
  cur = cur || 0;
  const target = p.target || 0;
  const pct = target > 0 ? Math.min(100, Math.round((cur / target) * 100)) : 0;
  return { cur, target, pct, unit: p.unit || METRICS[goal.metric]?.unit || '' };
}

/** Pace toward the due date, or null for a goal with no measure or no date. */
export function paceOf(goal, prog) {
  if (!prog || !goal.due_date) return null;
  const start = goal.start_date || stampDay(goal.created_at);
  const total = Math.max(1, daysBetween(start, goal.due_date) ?? 1);
  const elapsed = Math.max(0, Math.min(total, daysBetween(start, today()) ?? 0));
  const left = daysBetween(today(), goal.due_date);
  const remain = Math.max(0, prog.target - prog.cur);
  const perDay = left > 0 ? remain / left : remain;
  const expected = (prog.target * elapsed) / total;
  const hist = goal.history || [];
  const avg = hist.length ? hist.reduce((a, h) => a + (h.value || 0), 0) / hist.length : 0;
  return {
    left, remain, perDay, expected, avg,
    elapsedPct: (elapsed / total) * 100,
    behind: elapsed / total > 0.15 && prog.cur < expected * 0.7,
    ahead: prog.cur >= expected,
  };
}

/** notstarted | progress | risk | overdue | done | cancelled */
export function goalStatus(goal, live) {
  const s = serverState(goal);
  if (s === 'cancelled' || s === 'rejected') return 'cancelled';
  if (s === 'completed') return 'done';
  const prog = progressOf(goal, live);
  if (goal.due_date && daysBetween(today(), goal.due_date) < 0) return 'overdue';
  if (prog ? prog.cur <= 0 : s === 'unstarted') return 'notstarted';
  const pace = paceOf(goal, prog);
  if (pace && pace.behind) return 'risk';
  return 'progress';
}

export const isOpen = (goal) => !isClosed(goal);

export const fmtRate = (v) => (v < 10 ? v.toFixed(1).replace(/\.0$/, '') : Math.round(v).toLocaleString());

export function paceText(goal, live) {
  const s = goalStatus(goal, live);
  if (s === 'done' || s === 'cancelled') return '';
  const prog = progressOf(goal, live);
  const pace = paceOf(goal, prog);
  if (!pace) return '';
  if (s === 'overdue') return `${Math.round(pace.remain).toLocaleString()} ${prog.unit} still to go`;
  return `${fmtRate(pace.perDay)} ${prog.unit}/day to finish on time`;
}

export const byDue = (a, b) => (a.due_date || '9999').slice(0, 10).localeCompare((b.due_date || '9999').slice(0, 10));
const PRIORITY_ORDER = { HIGH: 0, MEDIUM: 1, LOW: 2 };
export const SORTS = {
  due: byDue,
  priority: (a, b) => (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1),
  az: (a, b) => (a.name || '').localeCompare(b.name || ''),
};

/** Open goals with a due date, nearest first: the right rail's list. */
export function deadlines(goals) {
  return goals
    .filter((g) => isOpen(g) && g.due_date)
    .sort(byDue)
    .map((g) => ({ goal: g, days: daysBetween(today(), g.due_date) }));
}

/** The SMART check rows: [letter, label, state y|n|w, text]. */
export function smartRows(goal, prog, pace, scopeLabel) {
  const unit = prog?.unit || METRICS[goal.metric]?.unit || '';
  const amb = pace && METRICS[goal.metric] && pace.perDay > METRICS[goal.metric].ambitious;
  const title = (goal.name || '').trim();
  return [
    ['S', 'Specific', title.length >= 3 ? 'y' : 'n', title ? `${title} — ${scopeLabel}` : 'Name what you will achieve'],
    ['M', 'Measurable', prog && prog.target > 0 ? 'y' : 'n',
      prog && prog.target > 0
        ? `${prog.target.toLocaleString()} ${unit}${METRICS[goal.metric]?.auto ? ' · tracked automatically' : ' · updated by hand'}`
        : 'Set a target number'],
    ['A', 'Achievable', !pace ? 'n' : amb ? 'w' : 'y',
      !pace ? 'Add a measure and a due date to check the pace'
        : amb ? `${Math.round(pace.perDay).toLocaleString()} ${unit}/day is ambitious. Consider a later date.`
          : `${fmtRate(pace.perDay)} ${unit}/day over ${Math.max(0, pace.left)} days`],
    ['R', 'Relevant', (goal.why || '').trim() ? 'y' : 'n', (goal.why || '').trim() || 'Add why this goal matters'],
    ['T', 'Time-bound', goal.due_date && (daysBetween(goal.start_date || goal.created_at, goal.due_date) ?? 0) > 0 ? 'y' : 'n',
      goal.due_date ? `${fmtDay(goal.start_date || goal.created_at)} → ${fmtDay(goal.due_date)}` : 'Set a deadline'],
  ];
}
